/**
 * Panel 4: Multi-Domain Fusion — Cross-domain correlation clusters
 *
 * Features:
 *   - Header stats row (total clusters, avg correlation, domain coverage)
 *   - Sort controls: by correlation strength, name, domain count
 *   - Domain filter buttons: AIR, SPACE, CYBER, GROUND, SEA, ALL
 *   - Cluster grid with correlation bars and domain badges
 *   - Detail panel with ASCII dependency diagram on selection
 *   - Correlation Matrix heatmap toggle (5x5 domain grid)
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { FusionCluster } from '../../../types'
import { DOMAIN_CONFIG, type Domain } from '../../../types'
import { getFusionClusters } from '../../../services/api'

/* ── Helpers ─────────────────────────────────────────────────────── */

function domainColor(d: string): string {
  return DOMAIN_CONFIG[d as Domain]?.color ?? '#6366f1'
}

const PATTERN_TYPE_LABELS: Record<string, string> = {
  operational_dependency: 'Operational Dependency',
  maintenance_cascade: 'Maintenance Cascade',
  kill_chain_dependency: 'Kill Chain Dependency',
  logistics_enablement: 'Logistics Enablement',
  fleet_aging: 'Fleet Aging',
  ace_dispersal: 'ACE Dispersal',
}

type SortKey = 'correlation' | 'name' | 'domains'

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'correlation', label: 'Correlation' },
  { key: 'name', label: 'Name' },
  { key: 'domains', label: 'Domain Count' },
]

const ALL_DOMAINS: Domain[] = ['AIR', 'SPACE', 'CYBER', 'GROUND', 'SEA']

function strengthColor(pct: number): string {
  if (pct >= 85) return '#22c55e'
  if (pct >= 70) return '#f59e0b'
  return '#ef4444'
}

function mcColor(mc: number): string {
  if (mc >= 0.75) return '#22c55e'
  if (mc >= 0.60) return '#f59e0b'
  return '#ef4444'
}

/* ── Dependency Diagram ──────────────────────────────────────────── */

function DependencyDiagram({ cluster }: { cluster: FusionCluster }) {
  const pairs = cluster.edge_pairs
  if (!pairs || pairs.length === 0) {
    return (
      <div className="text-xs text-term-textDim italic mt-2">
        No edge pair data available for dependency diagram.
      </div>
    )
  }

  const mcRates = cluster.mc_rates ?? {}

  return (
    <div className="mt-3 space-y-1">
      <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">
        Dependency Graph
      </div>
      <div className="bg-term-bgDark rounded border border-term-border/40 p-3 font-mono text-[11px] leading-relaxed overflow-x-auto">
        {pairs.map((edge, idx) => {
          const fromMC = mcRates[edge.from]
          const toMC = mcRates[edge.to]
          const rColor = edge.r >= 0.85 ? '#22c55e' : edge.r >= 0.70 ? '#f59e0b' : '#ef4444'

          // Truncate names to fit
          const fromName = edge.from.length > 14 ? edge.from.slice(0, 13) + '.' : edge.from
          const toName = edge.to.length > 14 ? edge.to.slice(0, 13) + '.' : edge.to

          return (
            <div key={idx} className="mb-3 last:mb-0">
              <div className="flex items-center gap-0 flex-wrap sm:flex-nowrap">
                {/* Left box */}
                <div className="inline-block border border-term-border rounded px-2 py-1 min-w-[140px]">
                  <div className="text-term-text font-bold">{fromName}</div>
                  <div className="text-term-textDim">{edge.from_role}</div>
                  {fromMC !== undefined && (
                    <div style={{ color: mcColor(fromMC) }}>
                      MC: {(fromMC * 100).toFixed(1)}%
                    </div>
                  )}
                </div>

                {/* Arrow with r value */}
                <div className="flex flex-col items-center mx-2 min-w-[80px]">
                  <span style={{ color: rColor }} className="font-bold text-[10px]">
                    r={edge.r.toFixed(2)}
                  </span>
                  <div className="flex items-center">
                    <span className="text-term-textDim">{'─'.repeat(4)}</span>
                    <span style={{ color: rColor }}>{'─'.repeat(4)}{'>'}</span>
                  </div>
                </div>

                {/* Right box */}
                <div className="inline-block border border-term-border rounded px-2 py-1 min-w-[140px]">
                  <div className="text-term-text font-bold">{toName}</div>
                  <div className="text-term-textDim">{edge.to_role}</div>
                  {toMC !== undefined && (
                    <div style={{ color: mcColor(toMC) }}>
                      MC: {(toMC * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── Correlation Matrix Heatmap ──────────────────────────────────── */

function CorrelationMatrix({ clusters }: { clusters: FusionCluster[] }) {
  // Build a 5x5 matrix of domain-to-domain average correlation strengths
  const matrix = useMemo(() => {
    const m: Record<string, Record<string, { sum: number; count: number }>> = {}
    for (const d1 of ALL_DOMAINS) {
      m[d1] = {}
      for (const d2 of ALL_DOMAINS) {
        m[d1][d2] = { sum: 0, count: 0 }
      }
    }

    for (const c of clusters) {
      const domains = c.domains.filter((d) => ALL_DOMAINS.includes(d as Domain))
      // For single-domain clusters, mark self-correlation
      if (domains.length === 1) {
        const d = domains[0]
        m[d][d].sum += c.correlation_strength
        m[d][d].count += 1
      }
      // For multi-domain clusters, mark each pair
      for (let i = 0; i < domains.length; i++) {
        for (let j = i + 1; j < domains.length; j++) {
          const a = domains[i]
          const b = domains[j]
          m[a][b].sum += c.correlation_strength
          m[a][b].count += 1
          m[b][a].sum += c.correlation_strength
          m[b][a].count += 1
        }
      }
    }

    // Also set self-correlation averages
    for (const d of ALL_DOMAINS) {
      if (m[d][d].count === 0) {
        // Check if domain has any clusters at all
        const domClusters = clusters.filter((c) => c.domains.includes(d))
        if (domClusters.length > 0) {
          const avg =
            domClusters.reduce((s, c) => s + c.correlation_strength, 0) / domClusters.length
          m[d][d] = { sum: avg, count: 1 }
        }
      }
    }

    return m
  }, [clusters])

  function cellValue(d1: string, d2: string): number | null {
    const cell = matrix[d1]?.[d2]
    if (!cell || cell.count === 0) return null
    return cell.sum / cell.count
  }

  function heatColor(val: number): string {
    // Dark red to bright green gradient
    if (val >= 0.85) return '#22c55e'
    if (val >= 0.75) return '#4ade80'
    if (val >= 0.65) return '#fbbf24'
    if (val >= 0.55) return '#f59e0b'
    return '#ef4444'
  }

  return (
    <div className="mt-4">
      <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">
        Cross-Domain Correlation Matrix
      </div>
      <div className="overflow-x-auto">
        <table className="border-collapse text-[11px] font-mono">
          <thead>
            <tr>
              <th className="p-1.5 text-term-textDim border-b border-term-border/40 text-left min-w-[64px]"></th>
              {ALL_DOMAINS.map((d) => (
                <th
                  key={d}
                  className="p-1.5 border-b border-term-border/40 text-center min-w-[60px]"
                  style={{ color: domainColor(d) }}
                >
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ALL_DOMAINS.map((row) => (
              <tr key={row}>
                <td
                  className="p-1.5 font-bold border-r border-term-border/40 text-right pr-2"
                  style={{ color: domainColor(row) }}
                >
                  {row}
                </td>
                {ALL_DOMAINS.map((col) => {
                  const val = cellValue(row, col)
                  const isDiag = row === col
                  return (
                    <td key={col} className="p-0.5 text-center">
                      {val !== null ? (
                        <div
                          className="rounded px-1.5 py-1 mx-auto"
                          style={{
                            backgroundColor: `${heatColor(val)}18`,
                            border: `1px solid ${heatColor(val)}40`,
                            color: heatColor(val),
                            fontWeight: isDiag ? 700 : 500,
                          }}
                        >
                          {val.toFixed(2)}
                        </div>
                      ) : (
                        <div className="text-term-border px-1.5 py-1">---</div>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3 mt-2 text-[10px] text-term-textDim">
        <span>Legend:</span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#22c55e' }} />
          {'>'}0.85
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#4ade80' }} />
          0.75-0.85
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#fbbf24' }} />
          0.65-0.75
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#f59e0b' }} />
          0.55-0.65
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }} />
          {'<'}0.55
        </span>
      </div>
    </div>
  )
}

/* ── Main Panel ──────────────────────────────────────────────────── */

export function FusionMapPanel() {
  const [clusters, setClusters] = useState<FusionCluster[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<SortKey>('correlation')
  const [domainFilter, setDomainFilter] = useState<Domain | 'ALL'>('ALL')
  const [showMatrix, setShowMatrix] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getFusionClusters()
      if (res.error) throw new Error(res.error)
      if (res.data) setClusters(res.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch fusion clusters')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  /* ── Derived state ──────────────────────────────────────────── */

  const filtered = useMemo(() => {
    if (domainFilter === 'ALL') return clusters
    return clusters.filter((c) => c.domains.includes(domainFilter))
  }, [clusters, domainFilter])

  const sorted = useMemo(() => {
    const copy = [...filtered]
    switch (sortKey) {
      case 'correlation':
        return copy.sort((a, b) => b.correlation_strength - a.correlation_strength)
      case 'name':
        return copy.sort((a, b) => a.name.localeCompare(b.name))
      case 'domains':
        return copy.sort((a, b) => b.domains.length - a.domains.length || b.correlation_strength - a.correlation_strength)
    }
  }, [filtered, sortKey])

  const stats = useMemo(() => {
    if (clusters.length === 0) return { total: 0, avgCorr: 0, domainsCovered: 0, maxCorr: 0, minCorr: 0 }
    const corrs = clusters.map((c) => c.correlation_strength)
    const allDomains = new Set(clusters.flatMap((c) => c.domains))
    return {
      total: clusters.length,
      avgCorr: corrs.reduce((s, v) => s + v, 0) / corrs.length,
      domainsCovered: allDomains.size,
      maxCorr: Math.max(...corrs),
      minCorr: Math.min(...corrs),
    }
  }, [clusters])

  // Domain counts for filter badges
  const domainCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const d of ALL_DOMAINS) {
      counts[d] = clusters.filter((c) => c.domains.includes(d)).length
    }
    return counts
  }, [clusters])

  const selected = clusters.find((c) => c.id === selectedCluster)

  /* ── Loading / Error ────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="flex flex-col gap-3 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-term-bgLight rounded border border-term-border/30" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-term-error text-lg">Error loading fusion clusters</div>
        <div className="text-term-textDim text-sm">{error}</div>
        <button onClick={fetchData} className="term-button term-button-primary">
          Retry
        </button>
      </div>
    )
  }

  /* ── Render ─────────────────────────────────────────────────── */

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* ═══ Header Stats Row ═══ */}
      <div className="grid grid-cols-5 gap-3">
        <div className="term-box p-2.5 text-center">
          <div className="text-2xl font-bold text-term-primary tabular-nums">{stats.total}</div>
          <div className="text-[10px] text-term-textDim uppercase tracking-wider">Clusters</div>
        </div>
        <div className="term-box p-2.5 text-center">
          <div
            className="text-2xl font-bold tabular-nums"
            style={{ color: strengthColor(Math.round(stats.avgCorr * 100)) }}
          >
            {stats.avgCorr.toFixed(2)}
          </div>
          <div className="text-[10px] text-term-textDim uppercase tracking-wider">Avg Correlation</div>
        </div>
        <div className="term-box p-2.5 text-center">
          <div className="text-2xl font-bold text-green-400 tabular-nums">
            {stats.maxCorr.toFixed(2)}
          </div>
          <div className="text-[10px] text-term-textDim uppercase tracking-wider">Strongest</div>
        </div>
        <div className="term-box p-2.5 text-center">
          <div className="text-2xl font-bold text-amber-400 tabular-nums">
            {stats.minCorr.toFixed(2)}
          </div>
          <div className="text-[10px] text-term-textDim uppercase tracking-wider">Weakest</div>
        </div>
        <div className="term-box p-2.5 text-center">
          <div className="text-2xl font-bold text-purple-400 tabular-nums">
            {stats.domainsCovered}/5
          </div>
          <div className="text-[10px] text-term-textDim uppercase tracking-wider">Domains</div>
        </div>
      </div>

      {/* ═══ Controls Row: Domain Filters + Sort + Matrix Toggle ═══ */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        {/* Domain filter buttons */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-term-textDim uppercase tracking-wider mr-1">Domain:</span>
          <button
            onClick={() => setDomainFilter('ALL')}
            className={`text-[11px] px-2 py-1 rounded border transition-all ${
              domainFilter === 'ALL'
                ? 'bg-term-primary/20 border-term-primary text-term-primary font-bold'
                : 'border-term-border/40 text-term-textDim hover:border-term-border'
            }`}
          >
            ALL ({clusters.length})
          </button>
          {ALL_DOMAINS.map((d) => {
            const isActive = domainFilter === d
            const color = domainColor(d)
            const count = domainCounts[d] ?? 0
            return (
              <button
                key={d}
                onClick={() => setDomainFilter(isActive ? 'ALL' : d)}
                className="text-[11px] px-2 py-1 rounded border transition-all"
                style={{
                  backgroundColor: isActive ? `${color}20` : 'transparent',
                  borderColor: isActive ? `${color}80` : 'rgba(255,255,255,0.1)',
                  color: isActive ? color : '#9ca3af',
                  fontWeight: isActive ? 700 : 400,
                }}
              >
                {d} ({count})
              </button>
            )
          })}
        </div>

        {/* Sort + Matrix toggle */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-term-textDim uppercase tracking-wider">Sort:</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setSortKey(opt.key)}
              className={`text-[11px] px-2 py-1 rounded border transition-all ${
                sortKey === opt.key
                  ? 'bg-term-primary/20 border-term-primary text-term-primary font-bold'
                  : 'border-term-border/40 text-term-textDim hover:border-term-border'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <div className="w-px h-5 bg-term-border/40 mx-1" />
          <button
            onClick={() => setShowMatrix((v) => !v)}
            className={`text-[11px] px-2.5 py-1 rounded border transition-all ${
              showMatrix
                ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400 font-bold'
                : 'border-term-border/40 text-term-textDim hover:border-term-border'
            }`}
          >
            {showMatrix ? '[x] Matrix' : '[ ] Matrix'}
          </button>
        </div>
      </div>

      {/* ═══ Correlation Matrix (toggleable) ═══ */}
      {showMatrix && <CorrelationMatrix clusters={clusters} />}

      {/* ═══ Showing N of M indicator ═══ */}
      {domainFilter !== 'ALL' && (
        <div className="text-[10px] text-term-textDim">
          Showing {sorted.length} of {clusters.length} clusters matching{' '}
          <span style={{ color: domainColor(domainFilter) }}>{domainFilter}</span> domain
        </div>
      )}

      {/* ═══ Cluster Grid ═══ */}
      <div className="grid grid-cols-3 gap-3">
        {sorted.map((cluster) => {
          const isSelected = selectedCluster === cluster.id
          const strengthPct = Math.round(cluster.correlation_strength * 100)
          const sColor = strengthColor(strengthPct)

          return (
            <div
              key={cluster.id}
              className={`term-box p-3 cursor-pointer transition-all duration-150 ${
                isSelected ? 'ring-1 ring-term-primary' : 'hover:bg-term-bgLight/50'
              }`}
              onClick={() => setSelectedCluster(isSelected ? null : cluster.id)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-term-textDim text-xs font-mono">{cluster.id}</span>
                <span className="text-xs font-bold tabular-nums" style={{ color: sColor }}>
                  r={cluster.correlation_strength.toFixed(2)}
                </span>
              </div>
              <div className="text-sm font-bold text-term-text mb-1 leading-tight">{cluster.name}</div>
              <div className="flex gap-1 mb-2 flex-wrap">
                {cluster.domains.map((d) => (
                  <span
                    key={d}
                    className="text-[10px] px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: `${domainColor(d)}20`,
                      color: domainColor(d),
                    }}
                  >
                    {d}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-1">
                {cluster.assets.map((a) => (
                  <span
                    key={a}
                    className="text-[9px] text-term-textDim bg-term-bgLight px-1.5 py-0.5 rounded"
                  >
                    {a}
                  </span>
                ))}
              </div>

              {/* Pattern type badge */}
              <div className="mt-2 mb-1">
                <span className="text-[9px] text-term-textDim bg-term-bgDark px-1.5 py-0.5 rounded border border-term-border/30">
                  {PATTERN_TYPE_LABELS[cluster.pattern_type] ?? cluster.pattern_type}
                </span>
              </div>

              {/* Correlation strength bar */}
              <div className="mt-1.5 h-1.5 bg-term-bgDark rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${strengthPct}%`, backgroundColor: sColor }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* ═══ Detail Panel ═══ */}
      {selected && (
        <div className="term-box p-4 flex-1">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-lg font-bold text-term-text">{selected.name}</div>
              <div className="text-xs text-term-textDim">
                {PATTERN_TYPE_LABELS[selected.pattern_type] ?? selected.pattern_type}
              </div>
            </div>
            <div className="text-right">
              <div
                className="text-2xl font-bold tabular-nums"
                style={{ color: strengthColor(Math.round(selected.correlation_strength * 100)) }}
              >
                {(selected.correlation_strength * 100).toFixed(0)}%
              </div>
              <div className="text-[10px] text-term-textDim">Correlation Strength</div>
            </div>
          </div>
          <div className="text-sm text-term-text leading-relaxed">{selected.description}</div>

          {/* Domains */}
          <div className="mt-3 flex gap-2 items-center">
            <span className="text-xs text-term-textDim">Domains:</span>
            {selected.domains.map((d) => (
              <span
                key={d}
                className="text-xs px-2 py-0.5 rounded"
                style={{
                  backgroundColor: `${domainColor(d)}20`,
                  color: domainColor(d),
                  border: `1px solid ${domainColor(d)}40`,
                }}
              >
                {d}
              </span>
            ))}
          </div>

          {/* Assets with MC rates */}
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <span className="text-xs text-term-textDim">Assets:</span>
            {selected.assets.map((a) => {
              const mc = selected.mc_rates?.[a]
              return (
                <span
                  key={a}
                  className="text-xs text-term-text bg-term-bgLight px-2 py-0.5 rounded border border-term-border inline-flex items-center gap-1.5"
                >
                  {a}
                  {mc !== undefined && (
                    <span className="text-[10px] font-mono" style={{ color: mcColor(mc) }}>
                      {(mc * 100).toFixed(1)}%
                    </span>
                  )}
                </span>
              )
            })}
          </div>

          {/* ASCII Dependency Diagram */}
          <DependencyDiagram cluster={selected} />
        </div>
      )}
    </div>
  )
}
